import config from 'config';
import deburr from 'lodash/deburr';
import { arrayOf, bool, func, object, oneOfType } from 'prop-types';
import React, { Fragment, PureComponent } from 'react';

import { atgBetTipsProvider, sportBetTipsProvider } from 'calendar/proptypes';
import Button from 'shared/components/button/Button';
import ComponentLoadingSection from 'shared/components/ComponentLoadingSection';
import Editorial from 'shared/components/content/Editorial';
import ErrorInfo from 'shared/components/ErrorInfo';
import HTMLContent from 'shared/components/HTMLContent';
import ProductName from 'shared/components/ProductName';
import { createShopUrl } from 'shared/utils';
import { formatCalendar, formatDate } from 'shared/utils/dateUtils';
import { getText } from 'shared/utils/Text';

export default class BetTips extends PureComponent {
  static propTypes = {
    tips: arrayOf(oneOfType([atgBetTipsProvider, sportBetTipsProvider]))
      .isRequired,
    error: object,
    isLoading: bool,
    onUseClick: func.isRequired,
    onBuyClick: func.isRequired,
  };

  onTipClick = (provider, event, tip) => {
    const { onUseClick, onBuyClick } = this.props;

    const { product_id, bet_type: betType, raceDate, trackName } = event;
    const { betId, name, paid } = tip;

    if (paid) {
      onUseClick(provider, betId);
    } else {
      const { spelvardeShopUrl, travogatShopUrl } = config;

      const url = createShopUrl(product_id);

      const title = getText(
        'tips-buy-title',
        `${betType} ${trackName} ${formatDate(raceDate)} – ${name}`,
      );

      let details = getText('tips-buy-details', provider);
      if (provider === 'Spelvärde') {
        details += getText(
          'tips-buy-access-card',
          betType,
          getText(spelvardeShopUrl),
          getText('tips-spelvarde'),
        );
      } else if (provider === 'Travögat') {
        details += getText(
          'tips-buy-access-card',
          betType,
          getText(travogatShopUrl),
          getText('tips-travogat'),
        );
      }

      onBuyClick(title, details, url);
    }
  };

  renderSportEvent(provider, event) {
    const { blurb, product, closeTime, betPublications } = event;

    return (
      <Fragment>
        <h2 className="tip-header">
          <ProductName product={product} />{' '}
          {closeTime && formatCalendar(closeTime, { showDayOfWeek: false })}
        </h2>
        {blurb && (
          <Editorial responsive={false}>
            <HTMLContent content={blurb.replace(/\n/g, '<br>')} />
          </Editorial>
        )}

        {betPublications.map(publication => (
          <Button
            key={publication.betId}
            className="tip-button"
            onClick={() => {
              this.props.onUseClick(provider, publication.betId);
            }}
          >
            {publication.name || ''}
            <div className="tip-button__action">
              {getText('tips-button-open')}
            </div>
          </Button>
        ))}
      </Fragment>
    );
  }

  renderAtgEvent(provider, event) {
    const { description, bet_type: betType, trackName, raceDate, tips } = event;
    return (
      <Fragment>
        <h2 className="tip-header">
          {betType} {trackName}{' '}
          {formatCalendar(raceDate, { showDayOfWeek: false })}
        </h2>
        <Editorial responsive={false}>
          <HTMLContent content={description} />
        </Editorial>

        {tips.map(tip => {
          const { betId, name, paid, price } = tip;

          return (
            <Button
              key={betId}
              className="tip-button"
              onClick={() => {
                this.onTipClick(provider, event, tip);
              }}
            >
              {name}
              <div className="tip-button__action">
                {paid && getText('tips-button-open')}
                {!paid &&
                  getText(
                    'tips-button-buy',
                    price != null
                      ? `– ${getText('general-currency-sek', price)}`
                      : '',
                  )}
              </div>
            </Button>
          );
        })}
      </Fragment>
    );
  }

  renderProviderEvents(provider, events) {
    if (events.length === 0) {
      return <p className="not-available">{getText('tips-not-available')}</p>;
    }

    return (
      <Fragment>
        {events.map(event => (
          <div key={event.id || event.product_id} className="tip-event">
            {(provider === 'Överodds' || provider === 'JokerSport') &&
              this.renderSportEvent(provider, event)}
            {provider !== 'Överodds' &&
              provider !== 'JokerSport' &&
              this.renderAtgEvent(provider, event)}
          </div>
        ))}
      </Fragment>
    );
  }

  render() {
    const { tips, error, isLoading } = this.props;

    return (
      <ComponentLoadingSection
        loading={isLoading}
        className="bet-tips aside-box"
      >
        <div className="tagline">{getText('tips-tagline')}</div>
        {error && <ErrorInfo error={error} />}
        {tips.length > 0 && (
          <ul className="provider-list">
            {tips.map(({ provider, events }) => (
              <li key={provider} className="provider-item">
                <img
                  className="provider-logo"
                  alt={provider}
                  src={`/images/${deburr(provider.toLowerCase())}_logo.png`}
                />
                {events != null && this.renderProviderEvents(provider, events)}
              </li>
            ))}
          </ul>
        )}
        <div className="footer">{getText('tips-footer')}</div>
      </ComponentLoadingSection>
    );
  }
}
